"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import GoogleButton from "../../components/GoogleButton";
import Icon from "../../components/Icon";
import { ApiError, api, setTokens } from "../../lib/api";
import { conNext, destinoSeguro } from "./destino";

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  roles: string[];
}

function Formulario() {
  const router = useRouter();
  const params = useSearchParams();
  // Cadena vacía = no pidieron destino; ahí manda la regla vieja (admin a /admin)
  const next = destinoSeguro(params.get("next"), "");
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [error, setError] = useState("");
  const [espera, setEspera] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setEspera(false);
    setBusy(true);
    try {
      const r = await api<AuthResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify(form),
      });
      setTokens(r.accessToken, r.refreshToken);
      // replace y no push: volver atrás desde el destino no debe recaer en el login
      router.replace(next || (r.roles.includes("ADMIN") ? "/admin" : "/perfil"));
      // Ojo: no se limpia `busy` en el camino feliz, así el botón queda quieto
      // mientras navega y nadie manda el formulario dos veces.
    } catch (err) {
      // El 429 ya viene redactado en español desde el API ("Esperá X minutos…"):
      // se muestra tal cual, en ámbar, porque no es un error del usuario.
      setEspera(err instanceof ApiError && err.status === 429);
      setError(err instanceof Error ? err.message : "No se pudo ingresar");
      setBusy(false);
    }
  }

  const input =
    "w-full rounded-xl bg-[#101516] border border-white/15 px-4 py-3 text-[15px] placeholder-[#808D88] focus:outline-none focus:border-[#E8A23C]";
  const label = "block text-sm text-[#B5C0BB] mb-1.5";

  return (
    <main className="min-h-screen bg-[#14191B] text-[#EDF1EF] flex items-center justify-center p-6">
      <form onSubmit={submit} className="w-full max-w-sm flex flex-col gap-5">
        <div>
          <p className="font-mono text-xs tracking-[0.2em] text-[#E8A23C] uppercase mb-2">Busco · ingresar</p>
          <h1 className="text-3xl font-bold tracking-tight">Hola de nuevo</h1>
          {next && (
            <p className="mt-2 text-sm text-[#B5C0BB]">Entrá y seguimos donde estabas.</p>
          )}
        </div>

        <div>
          <label htmlFor="identificador" className={label}>Teléfono o email</label>
          <input
            id="identificador"
            name="identificador"
            className={input}
            placeholder="09xxxxxxxx o tu@email.com"
            value={form.identifier}
            onChange={(e) => setForm({ ...form, identifier: e.target.value })}
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="clave" className={label}>Contraseña</label>
          <input
            id="clave"
            name="clave"
            className={input}
            type="password"
            placeholder="Tu contraseña"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            autoComplete="current-password"
            enterKeyHint="go"
            required
          />
        </div>

        {error && (
          <p
            role="alert"
            className={`text-sm rounded-lg px-4 py-2.5 flex items-start gap-2 ${
              espera ? "text-[#E8A23C] bg-[#E8A23C]/10" : "text-[#DE705C] bg-[#DE705C]/10"
            }`}
          >
            <Icon name={espera ? "clock" : "alert"} size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </p>
        )}

        <button disabled={busy}
          className="rounded-xl bg-[#E8A23C] text-[#201404] font-bold py-3.5 hover:brightness-105 transition disabled:opacity-50">
          {busy ? "Ingresando…" : "Ingresar"}
        </button>
        <Link href={conNext("/recuperar", next)} className="text-sm text-center text-[#808D88] hover:text-[#E8A23C] -mt-1">
          ¿Olvidaste tu contraseña?
        </Link>

        <GoogleButton next={next} />
        <p className="text-sm text-center text-[#808D88]">
          ¿No tenés cuenta?{" "}
          <Link href={conNext("/registro", next)} className="text-[#E8A23C] font-semibold">Registrate</Link>
        </p>
      </form>
    </main>
  );
}

// useSearchParams necesita un límite de Suspense para que la página siga siendo
// prerenderizable; el fallback es el fondo pelado para que no parpadee en blanco.
export default function Ingresar() {
  return (
    <Suspense fallback={<main className="min-h-screen bg-[#14191B]" />}>
      <Formulario />
    </Suspense>
  );
}
